import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getIn, useFormikContext } from 'formik';
import { FormControlLabel, RadioGroup, Radio, Checkbox, withStyles } from '@material-ui/core';
import { TextField } from '@avangard/ui/core';
import { black } from '@avangard/ui/colors';

import { UpdateStudentOnlineApplicationFormValues } from '@modules/students/requests';
import { StudentOnlineApplicationBlock } from '@modules/students/moleculas';
import {
    NeedToControlled,
    needToControlledTranslates,
    PreferToCommunicate,
    preferToCommunicateTranslates,
} from '@modules/students/constants';
import { getSpecialsGroupLabel, getSpecialsGroupPlaceholder } from '@modules/students/helpers';
import {
    StyledFormLabel,
    FormRow,
    Clarification,
    ClarificationTextField,
    StyledFormHelperText,
} from './styled';

const StudentOnlineApplicationSpecialsStep = () => {
    const { t } = useTranslation(['common']);

    const { values, errors, setFieldValue, handleChange, handleBlur } =
        useFormikContext<UpdateStudentOnlineApplicationFormValues>();

    const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        let valuesToSet: string[] = getIn(values, event.target.name);

        if (valuesToSet.includes(value)) {
            valuesToSet = valuesToSet.filter(item => item !== value);
        } else {
            valuesToSet = [...valuesToSet, value];
        }

        setFieldValue(name, valuesToSet);
    };

    const isAdultForSpecialsGroupLabel = values.isAdult || values.isFutureAdult;

    return (
        <>
            <StudentOnlineApplicationBlock title='Индивидуальные особенности'>
                <Content>
                    <TextField
                        fullWidth
                        required
                        id='individual.wantToServe'
                        label={getSpecialsGroupLabel('wantToServe', isAdultForSpecialsGroupLabel)}
                        placeholder='Например: Военно-воздушные силы'
                        value={values.individual.wantToServe}
                        error={!!errors.individual?.wantToServe}
                        helperText={errors.individual?.wantToServe}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='individual.sports'
                        label={getSpecialsGroupLabel('sports', isAdultForSpecialsGroupLabel)}
                        placeholder='Например: Футбол, баскетбол, волейбол'
                        value={values.individual.sports}
                        error={!!errors.individual?.sports}
                        helperText={errors.individual?.sports}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='individual.pleasureActivities'
                        label={getSpecialsGroupLabel(
                            'pleasureActivities',
                            isAdultForSpecialsGroupLabel,
                        )}
                        placeholder={getSpecialsGroupPlaceholder(
                            'pleasureActivities',
                            isAdultForSpecialsGroupLabel,
                        )}
                        value={values.individual.pleasureActivities}
                        error={!!errors.individual?.pleasureActivities}
                        helperText={errors.individual?.pleasureActivities}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        id='individual.specialSkills'
                        label={getSpecialsGroupLabel('specialSkills', isAdultForSpecialsGroupLabel)}
                        placeholder={getSpecialsGroupPlaceholder(
                            'specialSkills',
                            isAdultForSpecialsGroupLabel,
                        )}
                        value={values.individual.specialSkills}
                        error={!!errors.individual?.specialSkills}
                        helperText={errors.individual?.specialSkills}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='individual.unPleasureActivities'
                        label={getSpecialsGroupLabel(
                            'unPleasureActivities',
                            isAdultForSpecialsGroupLabel,
                        )}
                        placeholder={getSpecialsGroupPlaceholder(
                            'unPleasureActivities',
                            isAdultForSpecialsGroupLabel,
                        )}
                        value={values.individual.unPleasureActivities}
                        error={!!errors.individual?.unPleasureActivities}
                        helperText={errors.individual?.unPleasureActivities}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='individual.stressfulSituations'
                        label={getSpecialsGroupLabel(
                            'stressfulSituations',
                            isAdultForSpecialsGroupLabel,
                        )}
                        placeholder='Например: Ответственность за принятие важных решений'
                        value={values.individual.stressfulSituations}
                        error={!!errors.individual?.stressfulSituations}
                        helperText={errors.individual?.stressfulSituations}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='individual.attitudeThings'
                        label={getSpecialsGroupLabel(
                            'attitudeThings',
                            isAdultForSpecialsGroupLabel,
                        )}
                        placeholder={getSpecialsGroupPlaceholder(
                            'attitudeThings',
                            isAdultForSpecialsGroupLabel,
                        )}
                        value={values.individual.attitudeThings}
                        error={!!errors.individual?.attitudeThings}
                        helperText={errors.individual?.attitudeThings}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='individual.selfCare'
                        label='Способность к самообслуживанию (по 10-балльной шкале, где 0 означает «Неспособность к самообслуживанию», а 10 - «Полностью самостоятелен»)'
                        placeholder='Например: 8'
                        value={values.individual.selfCare}
                        error={!!errors.individual?.selfCare}
                        helperText={errors.individual?.selfCare}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <FormRow>
                        <StyledFormLabel>
                            Какие действия нужно проконтролировать (нужное отметить)
                        </StyledFormLabel>

                        <StyledFormControlLabel
                            control={
                                <Checkbox
                                    name='individual.needToControlled'
                                    value={NeedToControlled.HYGIENE}
                                    checked={values.individual.needToControlled.includes(
                                        NeedToControlled.HYGIENE,
                                    )}
                                    onChange={handleChangeCheckbox}
                                />
                            }
                            label={needToControlledTranslates[NeedToControlled.HYGIENE]}
                        />

                        <StyledFormControlLabel
                            control={
                                <Checkbox
                                    name='individual.needToControlled'
                                    value={NeedToControlled.CHANGE_CLOTHES}
                                    checked={values.individual.needToControlled.includes(
                                        NeedToControlled.CHANGE_CLOTHES,
                                    )}
                                    onChange={handleChangeCheckbox}
                                />
                            }
                            label={needToControlledTranslates[NeedToControlled.CHANGE_CLOTHES]}
                        />

                        <StyledFormControlLabel
                            control={
                                <Checkbox
                                    name='individual.needToControlled'
                                    value={NeedToControlled.DRYING_CLOTHES}
                                    checked={values.individual.needToControlled.includes(
                                        NeedToControlled.DRYING_CLOTHES,
                                    )}
                                    onChange={handleChangeCheckbox}
                                />
                            }
                            label={needToControlledTranslates[NeedToControlled.DRYING_CLOTHES]}
                        />

                        <StyledFormControlLabel
                            control={
                                <Checkbox
                                    name='individual.needToControlled'
                                    value={NeedToControlled.OTHER}
                                    checked={values.individual.needToControlled.includes(
                                        NeedToControlled.OTHER,
                                    )}
                                    onChange={handleChangeCheckbox}
                                />
                            }
                            label={needToControlledTranslates[NeedToControlled.OTHER]}
                        />

                        {values.individual.needToControlled.includes(NeedToControlled.OTHER) ? (
                            <Clarification>
                                <ClarificationTextField
                                    id='individual.needToControlledOther'
                                    placeholder='Введите действие, которое нужно проконтролировать'
                                    value={values.individual.needToControlledOther}
                                    error={!!errors.individual?.needToControlledOther}
                                    helperText={errors.individual?.needToControlledOther}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </Clarification>
                        ) : null}
                    </FormRow>

                    <FormRow>
                        <StyledFormLabel required>С кем предпочитаете общаться?</StyledFormLabel>

                        <RadioGroup
                            row
                            name='individual.preferCommunicate'
                            value={values.individual.preferCommunicate}
                            onChange={handleChange}
                        >
                            <FormControlLabel
                                value={PreferToCommunicate.YOUNGER}
                                label={t(
                                    preferToCommunicateTranslates[PreferToCommunicate.YOUNGER],
                                )}
                                control={<Radio />}
                            />

                            <FormControlLabel
                                value={PreferToCommunicate.PEERS}
                                label={t(preferToCommunicateTranslates[PreferToCommunicate.PEERS])}
                                control={<Radio />}
                            />

                            <FormControlLabel
                                value={PreferToCommunicate.OLDER}
                                label={t(preferToCommunicateTranslates[PreferToCommunicate.OLDER])}
                                control={<Radio />}
                            />
                        </RadioGroup>

                        {!!errors.individual?.preferCommunicate ? (
                            <StyledFormHelperText error>
                                {errors.individual?.preferCommunicate}
                            </StyledFormHelperText>
                        ) : null}
                    </FormRow>

                    <TextField
                        fullWidth
                        required
                        id='individual.communationDifficulties'
                        label='Есть ли трудности в общении?'
                        placeholder='Например: Бывает сложно поддерживать разговор в больших группах'
                        value={values.individual.communationDifficulties}
                        error={!!errors.individual?.communationDifficulties}
                        helperText={errors.individual?.communationDifficulties}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='individual.badHabits'
                        label='Отношение к курению и употреблению спиртных напитков'
                        placeholder={getSpecialsGroupPlaceholder(
                            'badHabits',
                            isAdultForSpecialsGroupLabel,
                        )}
                        value={values.individual.badHabits}
                        error={!!errors.individual?.badHabits}
                        helperText={errors.individual?.badHabits}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />

                    <TextField
                        fullWidth
                        required
                        id='individual.characterTraits'
                        label={getSpecialsGroupLabel(
                            'characterTraits',
                            isAdultForSpecialsGroupLabel,
                        )}
                        placeholder='Например: Ответственность, Целеустремлённость, Коммуникабельность'
                        value={values.individual.characterTraits}
                        error={!!errors.individual?.characterTraits}
                        helperText={errors.individual?.characterTraits}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </Content>
            </StudentOnlineApplicationBlock>
        </>
    );
};

const StyledFormControlLabel = withStyles(() => ({
    root: {
        width: '100%',
        marginRight: 24,
    },
    label: {
        color: black[80],
    },
}))(FormControlLabel);

const Content = styled.div`
    & > *:not(:last-child) {
        margin-bottom: 24px;
    }
`;

export { StudentOnlineApplicationSpecialsStep };
